import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { currencies } from "../../../utilities/RequestData.js";
import {
  fetchPaymentRequestDetails,
  updatePaymentRequest,
  submitPaymentRequest,
} from "../../../../contents/redux/actions/paymentRequestActions";
import { fetchPaymentProjects } from "../../../../contents/redux/actions/paymentProjectActions.js";
import { fetchPaymentPurposes } from "../../../../contents/redux/actions/paymentPurposeActions.js";
import {
  WorkRounded,
  LiveHelpRounded,
  MonetizationOnRounded,
  PaymentsRounded,
  EditRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { toast } from "react-toastify";
import styles from "../../../utilities/cssmodules/EditPaymentRequest.module.css";

const EditPaymentRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestId, requestType, requestTypeId } = location.state || {};
  console.log("Location state:", location.state);
  console.log("Payment Voucher ID:", requestTypeId);
  console.log("Payment Voucher requestId:", requestId);
  const userId = localStorage.getItem("userId");
  const dispatch = useDispatch();
  const [inputs, setInputs] = useState({
    project: "",
    purpose: "",
    amount: "",
    currency: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Redux state
  const paymentRequestDetails = useSelector(
    (state) => state.paymentRequest.paymentRequestDetails || null,
  );
  const { paymentProjects, loading: projectsLoading } = useSelector(
    (state) => state.paymentProjects || {},
  );
  const { paymentPurposes, Loading: purposesLoading } = useSelector(
    (state) => state.paymentPurposes || {},
  );

  // Fetch request details
  useEffect(() => {
    if (!requestId || !requestType) {
      toast.error("Request ID or Request Type is missing.");
      navigate("/myrequests");
      return;
    }
    dispatch(fetchPaymentRequestDetails(requestId, requestType));
  }, [dispatch, requestId, requestType, navigate]);

  // Fetch projects and purposes
  useEffect(() => {
    dispatch(fetchPaymentProjects());
    dispatch(fetchPaymentPurposes());
  }, [dispatch]);

  // Prepopulate form with paymentRequestDetails
  useEffect(() => {
    if (
      paymentRequestDetails &&
      paymentProjects &&
      paymentPurposes &&
      !projectsLoading &&
      !purposesLoading
    ) {
      console.log(
        "Prepopulating form with paymentRequestDetails:",
        paymentRequestDetails,
      );
      const selectedProject = paymentProjects.find(
        (project) => project.name === paymentRequestDetails.project,
      );
      const selectedPurpose = paymentPurposes.find(
        (purpose) => purpose.name === paymentRequestDetails.purpose,
      );
      setInputs({
        project: selectedProject ? selectedProject.id : "",
        purpose: selectedPurpose ? selectedPurpose.id : "",
        amount: paymentRequestDetails.amount?.toString() || "",
        currency: paymentRequestDetails.currency || "",
        description: paymentRequestDetails.description || "",
      });
    }
  }, [
    paymentRequestDetails,
    paymentProjects,
    paymentPurposes,
    projectsLoading,
    purposesLoading,
  ]);

  // Validate form
  useEffect(() => {
    const isValid =
      inputs.project &&
      inputs.purpose &&
      inputs.amount &&
      !isNaN(parseFloat(inputs.amount)) &&
      parseFloat(inputs.amount) > 0 &&
      inputs.currency &&
      inputs.description.trim();
    setIsFormValid(isValid);
  }, [inputs]);

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  // Handle save form
  const handleSave = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }
    if (!requestId) {
      toast.error("Request ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    setIsSubmitting(true);
    const updatedData = {
      projectId: parseInt(inputs.project),
      purposeId: parseInt(inputs.purpose),
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      description: inputs.description.trim(),
      requestTypeId: parseInt(requestTypeId),
      userId: parseInt(userId),
      isOnSaved: true,
    };

    dispatch(updatePaymentRequest(requestId, updatedData))
      .then(() => {
        setIsSubmitting(false);
        toast.success("Payment request saved successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        setIsSubmitting(false);
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.response?.data?.message || error.message;
        toast.error(`Failed to save payment request: ${errorMessage}`);
      });
  };

  // Handle submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }
    if (!requestId) {
      toast.error("Request ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    setIsSubmitting(true);
    const updatedData = {
      projectId: parseInt(inputs.project),
      purposeId: parseInt(inputs.purpose),
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      description: inputs.description.trim(),
      requestTypeId: parseInt(requestTypeId),
      userId: parseInt(userId),
      isOnSaved: false,
    };

    dispatch(updatePaymentRequest(requestId, updatedData))
      .then(() => {
        dispatch(submitPaymentRequest(requestId));
      })
      .then(() => {
        setIsSubmitting(false);
        toast.success("Payment request submitted successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        setIsSubmitting(false);
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.response?.data?.message || error.message;
        toast.error(`Failed to submit payment request: ${errorMessage}`);
      });
  };

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>
        <div className={styles["flex-row-container"]}>
          <div className={styles["flex-row-custom-1"]}>
            <span
              className={styles["title-back-button"]}
              onClick={goBack}
              aria-label="Go back to previous page"
            >
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span>Edit Payment Request</span>
          </div>
        </div>
      </div>
      <div className={styles["generic-activity-component-description"]}>
        Edit this payment request by updating the form below with the desired
        information and submitting when you are done. Ensure all fields have
        accurate information.
      </div>
      {(projectsLoading || purposesLoading) && (
        <div className={styles["loading-message"]}>
          Loading request details...
        </div>
      )}
      {paymentProjects?.length === 0 && !projectsLoading && (
        <div className={styles["error-message"]}>
          No projects available. Please contact support.
        </div>
      )}
      {paymentPurposes?.length === 0 && !purposesLoading && (
        <div className={styles["error-message"]}>
          No purposes available. Please contact support.
        </div>
      )}
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={handleSubmit}
        >
          <label className={styles["form-label"]} htmlFor="editrequestproject">
            <WorkRounded /> Project
          </label>
          <section className={styles["form-section"]}>
            <select
              name="project"
              id="editrequestproject"
              required
              value={inputs.project || ""}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Project"
              disabled={projectsLoading || paymentProjects?.length === 0}
            >
              <option value="">Select a project for the request</option>
              {Array.isArray(paymentProjects) &&
                paymentProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
            </select>
          </section>

          <label className={styles["form-label"]} htmlFor="editrequestpurpose">
            <LiveHelpRounded /> Purpose
          </label>
          <section className={styles["form-section"]}>
            <select
              name="purpose"
              id="editrequestpurpose"
              required
              value={inputs.purpose || ""}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Purpose"
              disabled={purposesLoading || paymentPurposes?.length === 0}
            >
              <option value="">Select a purpose for the request</option>
              {Array.isArray(paymentPurposes) &&
                paymentPurposes.map((purpose) => (
                  <option key={purpose.id} value={purpose.id}>
                    {purpose.name}
                  </option>
                ))}
            </select>
          </section>

          <label className={styles["form-label"]} htmlFor="editrequestamount">
            <MonetizationOnRounded /> Amount
          </label>
          <section className={styles["form-section"]}>
            <input
              type="number"
              name="amount"
              id="editrequestamount"
              placeholder="Enter amount for request"
              required
              value={inputs.amount || ""}
              onChange={handleChange}
              min="0"
              step="0.01"
              className={styles["form-input"]}
              aria-label="Amount"
            />
          </section>

          <label className={styles["form-label"]} htmlFor="editrequestcurrency">
            <PaymentsRounded /> Currency
          </label>
          <section className={styles["form-section"]}>
            <select
              name="currency"
              id="editrequestcurrency"
              required
              value={inputs.currency || ""}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Currency"
            >
              <option value="">Select a currency for the amount</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
          </section>

          <label
            className={styles["form-label"]}
            htmlFor="editrequestdescription"
          >
            <EditRounded /> Description
          </label>
          <section className={styles["form-section"]}>
            <textarea
              name="description"
              id="editrequestdescription"
              placeholder="Enter description for request"
              required
              value={inputs.description || ""}
              onChange={handleChange}
              className={styles["form-textarea"]}
              aria-label="Description"
            />
          </section>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={
                !isFormValid ||
                isSubmitting ||
                projectsLoading ||
                purposesLoading
              }
              aria-label="Save payment request"
            >
              Save
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={
                !isFormValid ||
                isSubmitting ||
                projectsLoading ||
                purposesLoading
              }
              aria-label="Submit payment request"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentRequest;
